import { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '../../../dataProvider';
import { handleAPIError } from '../../Signet/Studio/hooks/useEngravingAPI';
import { useNotify } from 'react-admin';
import { useDesignPatternStudio } from '../DesignPatternStudioContext';
import { ACTIONS } from '../reducers/studioReducer';
import { createMappingStructure } from '../utils/mappingUtils';

const useDesignPatternAPI = () => {
    
    const [loading, setLoading] = useState(false);
    const notify = useNotify();
    const { state, dispatch } = useDesignPatternStudio();

    const fetchDesignPatterns = useCallback(async () => {
        if(state.designPatterns.length > 0) return;
        setLoading(true);   
        try {
            const response = await axios.get(`${apiUrl}/get-design-patterns`);
            if (response?.data) {
                dispatch({ type: ACTIONS.SET_DESIGN_PATTERNS, payload: response.data.design_patterns });
            }
        } catch (error) {
            handleAPIError(error, notify);
        } finally {
            setLoading(false);
        }
    }, [dispatch, notify, state.designPatterns]);

    const fetchProfileConfigs = useCallback(async () => {
        if(state.profileConfigs) return;
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/get-ring-config`);
            if (response?.data) {
                dispatch({ type: ACTIONS.SET_PROFILE_CONFIGS, payload: response.data.ring_config });
            }
        } catch (error) {
            handleAPIError(error, notify);
        } finally {
            setLoading(false);
        }
    }, [dispatch, notify, state.profileConfigs]);

    const updateProfileConfig = useCallback(async (id, profile, width, widthConfig) => {
        setLoading(true);
        try{
            const response = await axios.put(`${apiUrl}/profile-width-option-config/${id}`, {
                profile,
                width,
                config: widthConfig,
            });
            if(response?.status === 200) {
                notify(`Configuration for Profile:${profile}, Width:${width} saved successfully`, { type: 'success' });
            }
        } catch (error) {
            handleAPIError(error, notify);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    const createProfileConfig = useCallback(async ( profile, width, profileConfig) => {
        setLoading(true);
        try{
            await axios.post(`${apiUrl}/profile-width-option-config`, {
                profile,
                width,
                base_config: profileConfig,
            });
        } catch (error) {
            handleAPIError(error, notify);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    const fetchDesignPatternData = useCallback(async (designPatternId, insideFeature = 0) => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/get-design-pattern-data/${designPatternId}`, { 
                params: { 
                    insideFeature: insideFeature 
                } 
            });
            if (response?.data) {
                if(response.data.materials) {
                    // Extract unique colors from all materials
                    const uniqueColors = response.data.materials.reduce((acc, material) => {
                        if (material.color_options) {
                            material.color_options.forEach(color => {
                                if (!acc.some(c => c.sku_code === color.sku_code)) {
                                    acc.push(color);
                                }
                            });
                        }
                        return acc;
                    }, []);
                    const updatedMapping = createMappingStructure(response.data.materials, uniqueColors);
                    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMapping});
                }
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                handleAPIError(error, notify);
            }
        } finally {
            setLoading(false);
        }
    }, [dispatch, notify]);

    const fetchProfilesData = useCallback(async () => {
        if(state.profiles.length > 0) return;
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/get-profile-width-option-data`);
            if (response?.data) {
                dispatch({ type: ACTIONS.SET_PROFILES, payload: response.data.profile_width_option_data });
            }
        } catch (error) {
            handleAPIError(error, notify);
        } finally {
            setLoading(false);
        }
    }, [dispatch, notify, state.profiles]);

    useEffect(() => { 
        fetchProfileConfigs();
        fetchDesignPatterns();
    }, [fetchProfileConfigs, fetchDesignPatterns]);

    return { loading, fetchDesignPatternData, createProfileConfig, updateProfileConfig, fetchProfilesData };
}

export default useDesignPatternAPI;